import { SituacaoEnum } from '@/utils/enumerados';
import elementoVisivel from '../dom';

const SELECTOR_SITUACAO = '[data-tour="situacao"]';
const SELECTOR_ABRIR = '[data-tour="aguardando-inicio-abrir-sessao"]';
const SELECTOR_CHAT = '[data-tour="aguardando-inicio-chat"]';
const SELECTOR_CHAT_MODO = '[data-tour="aguardando-inicio-chat-modo"]';
const SELECTOR_CHAT_PARTICIPANTES_BUTTON = '[data-tour="aguardando-inicio-chat-participantes"]';
const SELECTOR_CHAT_HEADER = '.chat-header';
const SELECTOR_CHAT_CONTENT = '.chat-content';
const CHAT_ATIVA_CLASS = 'chat-ativo';
const SELECTOR_PARTICIPANTS_PANEL = '.participants-panel';
const SELECTOR_BLOCK_CONTROL = '.participant-block-control';
const SELECTOR_MESSAGE_FIELD = '.insert-message';

const CAN_MESSAGE_NOW_DESCRIPTION =
  'O chat já está disponível para você se comunicar oficialmente com os licitantes. Eles só poderão enviar mensagens a partir da abertura da sessão.';

// AuctionChat.vue has no external API to expand its chat/participants panels — isActive and
// viewParticipants are local data() properties toggled only by real clicks on their headers.
// Each reveal happens from the PRECEDING step's onNextClick (not upfront), so panels only
// open exactly when the guide reaches that part — tourRunner's waitForElement (see below)
// is what lets a step whose target doesn't exist *yet* still make it into the tour.
function expandChatIfNeeded() {
  const content = document.querySelector(SELECTOR_CHAT_CONTENT);
  if (content && !content.classList.contains(CHAT_ATIVA_CLASS)) {
    document.querySelector(SELECTOR_CHAT_HEADER)?.click();
  }
}

function expandParticipantsIfNeeded() {
  if (!document.querySelector(SELECTOR_PARTICIPANTS_PANEL)) {
    document.querySelector(SELECTOR_CHAT_PARTICIPANTES_BUTTON)?.click();
  }
}

export default {
  id: 'aguardando-inicio-sessao',
  kind: 'tour',
  version: 2,
  label: 'Aguardando início da sessão',
  contexts: ['auction.situation'],
  situation: SituacaoEnum.AGUARDANDO_INICIO_SESSAO,
  priority: 10,
  help: true,
  canStart(ctx = {}) {
    if (!ctx.logged) return false;
    if (Number(ctx.situation) !== SituacaoEnum.AGUARDANDO_INICIO_SESSAO) return false;
    return elementoVisivel(SELECTOR_SITUACAO);
  },
  // ctx.isCrier is the same signal AuctionContainer.vue uses to decide who can actually open
  // the session (displayOpenSession) — pure role (PREGOEIRO, which getFunction also collapses
  // AGENTE-CONTRATACAO into), unlike elementoVisivel(SELECTOR_ABRIR) which also depends on
  // whether the scheduled opening time has passed yet. Only the crier gets mode/moderation
  // steps (nobody else can act on them here) — everyone else just gets told to wait, plus a
  // Chat step, since some of them (Autoridade, substitutos) can actually message already.
  steps(ctx = {}) {
    const steps = [
      {
        element: SELECTOR_SITUACAO,
        popover: {
          title: 'Aguardando início da sessão',
          description: ctx.isCrier
            ? 'A data de abertura já foi atingida — é por isso que a sessão está nesta etapa. Confira se há esclarecimentos ou impugnações pendentes de resposta antes de abrir a sessão.'
            : 'A entrega de propostas foi encerrada. Nesta fase a sessão pública ainda não começou — aguarde a abertura pelo pregoeiro.',
          side: 'bottom',
          align: 'center',
        },
      },
    ];

    if (!ctx.isCrier) {
      // canMessageBeforeSessionStarts (AuctionChat.vue) also covers Autoridade and
      // Pregoeiro/Agente de Contratação substitutos, not just literal PREGOEIRO — so some
      // non-crier readers of this step actually can message already, others (licitantes)
      // can't yet. .insert-message only renders once the field itself is allowed.
      if (elementoVisivel(SELECTOR_CHAT)) {
        const canMessageNow = elementoVisivel(SELECTOR_MESSAGE_FIELD);
        steps.push({
          element: SELECTOR_CHAT,
          popover: {
            title: 'Chat da sessão',
            description: canMessageNow
              ? CAN_MESSAGE_NOW_DESCRIPTION
              : 'O chat já está visível, mas o envio de mensagens só fica liberado a partir da abertura da sessão pelo pregoeiro.',
            side: 'left',
            align: 'start',
          },
        });
      }
      return steps;
    }

    if (elementoVisivel(SELECTOR_ABRIR)) {
      steps.push({
        element: SELECTOR_ABRIR,
        popover: {
          title: 'Abrir Sessão',
          description:
            'Use este botão para iniciar a sessão pública e avançar para a análise das propostas. Se houver esclarecimentos ou impugnações sem resposta, a abertura será bloqueada até que sejam respondidos.',
          side: 'bottom',
          align: 'center',
        },
      });
    }

    // .chat-mode-toolbar has no isActive gate — it stays in the DOM even while the chat panel
    // is visually collapsed, so this reliably tells us whether THIS user can moderate at all,
    // independent of whatever expand state the panel happens to be in right now.
    const canModerate = elementoVisivel(SELECTOR_CHAT_MODO);

    if (elementoVisivel(SELECTOR_CHAT)) {
      const chatStep = {
        element: SELECTOR_CHAT,
        popover: {
          title: 'Chat da sessão',
          description: CAN_MESSAGE_NOW_DESCRIPTION,
          side: 'left',
          align: 'start',
        },
      };

      if (canModerate) {
        // Only expand right when leaving this step — not before — so the panel visibly
        // opens as part of the guide reaching this point, not upfront.
        chatStep.popover.onNextClick = (element, step, { driver }) => {
          expandChatIfNeeded();
          driver.moveNext();
          setTimeout(() => driver.refresh(), 320); // settle position after the CSS transition
        };
      }

      steps.push(chatStep);
    }

    if (canModerate) {
      steps.push({
        element: SELECTOR_CHAT_MODO,
        popover: {
          title: 'Modo do chat',
          description:
            'Você já pode definir aqui o modo de envio de mensagens dos licitantes, antes mesmo da sessão abrir: "Todos" libera qualquer licitante a enviar mensagem; "Somente melhor oferta" libera só quem estiver com a melhor oferta vigente em algum item, a partir do Julgamento; "Nenhum" bloqueia o envio para todos os licitantes.',
          side: 'top',
          align: 'start',
          // Reveal Participantes only now, moving into the Bloqueio step — its target won't
          // exist in the DOM yet at this point, which is exactly what waitForElement below is
          // for: tourRunner won't drop the step for that, and driver.js polls until it shows.
          onNextClick: (element, step, { driver }) => {
            expandParticipantsIfNeeded();
            driver.moveNext();
          },
        },
      });

      steps.push({
        element: SELECTOR_BLOCK_CONTROL,
        waitForElement: 1000,
        popover: {
          title: 'Bloqueio individual',
          description:
            'Abrindo Participantes você também pode bloquear um licitante específico para envio de mensagens, já nesta etapa.',
          side: 'bottom',
          align: 'start',
        },
      });
    }

    return steps;
  },
};
